<script>
import appConfig from '@src/app.config'
import { authComputed } from '@state/helpers'

import GuestLayout from '@layouts/guest.vue'
import SafeHaven from '@layouts/safehaven.vue'
import SafeHavenAuth from '@layouts/safehaven-auth.vue'
import Admin from '@layouts/admin.vue'
import SessionTimeAlert from '@components/alerts/session-time-alert.vue'

export default {
  page: {
    // All subcomponent titles will be injected into this template.
    titleTemplate(title) {
      title = typeof title === 'function' ? title(this.$store) : title
      return title ? `${title} | ${appConfig.title}` : appConfig.title
    },
  },
  components: {
    SessionTimeAlert,
  },
  data() {
    return {
      staticRoutes: [
        'library',
        'settings.profile',
        'settings.password',
        'settings.call-center',
        'settings.family-members',
        'settings.privacy',
        'settings.2fa',
        'settings.contract',
        'settings.billing',
      ],
    }
  },
  computed: {
    ...authComputed,
    path() {
      return this.staticRoutes.includes(this.$route.name)
        ? 'static'
        : this.$route.fullPath
    },
    LayoutComponent() {
      document.body.removeAttribute('class', 'noscroll')
      const clientId = this.$route.params.clientId
      if (this.$route.meta && this.$route.meta.layout) {
        // custom layout
        return this.$route.meta.layout
      } else if (
        this.$route.path.includes(clientId) &&
        this.$route.path.includes('/addUser/')
      ) {
        return Admin
      } else if (this.$route.path.includes(clientId) && this.fullAccess) {
        return SafeHavenAuth
      } else if (this.$route.path.includes(clientId)) {
        return SafeHaven
      } else {
        // default guest layout
        return GuestLayout
      }
    },
    isIdle() {
      return this.$store.state.idleVue.isIdle
    },
    isSessionAlert() {
      return this.$store.state.auth.sessionAlert
    },
  },
  watch: {
    isIdle: function () {
      if (this.loggedIn && this.isIdle && !this.isSessionAlert) {
        this.$store.dispatch('auth/setSessionAlertStatus', true)
      }
    },
  },
}
</script>

<template>
  <!-- vuetify wrapper -->
  <v-app id="app">
    <SessionTimeAlert v-if="isSessionAlert" :show-alert="isSessionAlert" />
    <!--
    Even when routes use the same component, treat them
    as distinct and create the component again.
    As an exception in '/library' and '/settings' route you want the route to be static.
    -->
    <transition name="fade" mode="out-in">
      <component
        :is="LayoutComponent"
        :key="LayoutComponent.name || LayoutComponent.__file"
      >
        <transition name="fade" mode="out-in">
          <!--
          todo: keep alive here to keep components' state when needed,
          or create nested routes with router-view and keep alive there
          e.g. below keep alive only health record view with external datatables
          -->
          <!-- <keep-alive include="HealthRecordIndex"> -->
          <router-view :key="path" />
          <!-- </keep-alive> -->
        </transition>
      </component>
    </transition>
  </v-app>
</template>

<!-- This should generally be the only global CSS in the app. -->
<style lang="scss">
// Allow element/type selectors, because this is global CSS.
// stylelint-disable selector-max-type, selector-class-pattern

// Normalize default styles across browsers,
// https://necolas.github.io/normalize.css/
@import '~normalize.css/normalize.css';
// Style loading bar between pages.
// https://github.com/rstacruz/nprogress
@import '~nprogress/nprogress.css';

// Design variables and utilities from src/design.
@import '@design';

*,
*::before,
*::after {
  box-sizing: border-box;
}

body {
  background: #f2f9ff;
}

.noscroll {
  position: fixed;
  overflow-x: hidden;
  overflow-y: hidden;
}

// ===
// Base element styles
// ===

a,
a:visited {
  color: $color-link-text;
}

h1 {
  @extend %typography-xxlarge;
}

h2 {
  @extend %typography-xlarge;
}

h3 {
  @extend %typography-large;
}

h4 {
  @extend %typography-medium;
}

h5,
h6 {
  @extend %typography-small;
}

// ===
// Vendor
// ===

#nprogress .bar {
  background: $color-loading-bar;
}

.v-treeview-node {
  cursor: pointer;
}

.v-treeview-node__root.v-treeview-node--active {
  pointer-events: none;
}

.v-treeview-node__toggle,
.v-treeview-actions {
  pointer-events: all;
}

.v-data-table tr {
  cursor: pointer;
}

.v-application--is-ltr
  .v-list--dense.v-list--nav
  .v-list-group--no-action
  > .v-list-group__items
  > .v-list-item {
  padding-left: 30px !important;
}

// hide element
.v-avatar.hidden {
  display: none !important;
}

.row.height-timeline {
  min-height: 25px;
}

// remove effect when clicking card
.v-card--link::before {
  background: none !important;
}

// colored slider

.v-slider__track-container {
  height: 12px !important;
}

.v-slider__track-background {
  border-radius: 40px !important;
}

.v-slider__track-fill {
  background: linear-gradient(
    95.08deg,
    #00c7ff 2.49%,
    #ab2eff 46.38%,
    #ff3b25 97.19%
  ) !important;
  border-radius: 40px !important;
}

.uploadDoc .v-input__control {
  display: none;
}

.uploadDoc .mdi::before,
.mdi-set {
  font-size: 32px !important;
  color: #0476d9;
}

.uploadDoc .v-icon.v-icon::after {
  border-radius: 0 !important;
}

.uploadDoc .theme--light.v-icon:focus::after {
  opacity: 0 !important;
}

.uploadDoc {
  display: block !important;
  align-items: center !important;
  padding-top: 5px !important;
  margin: 0 !important;
  text-align: center !important;
}

.uploadDoc:disabled {
  cursor: not-allowed;
}

.v-slider__thumb {
  left: -16px !important;
  width: 32px !important;
  height: 32px !important;
}

.v-slider__thumb.primary {
  border: 2px solid $color-theme-light-primary-shadow !important;
}

.v-slider__thumb::before {
  top: -4px !important;
  left: -4px !important;
}

.v-slider__thumb-label {
  margin-bottom: 10px !important;
}

// Vuetify Font Override for specific classes + global background color

$typo-options: display-4, display-3, display-2, display-1, headline, title,
  subtitle-1, subtitle-2, body-1, body-2, caption, overline, text-h1, text-h2,
  text-h3, text-h4, text-overline;
#app {
  @extend %typography-small;

  font-family: $system-default-font-family !important;
  background: $color-theme-light-bg;
  @each $typo-option in $typo-options {
    .#{$typo-option} {
      font-family: $system-default-font-family !important;
    }
  }
}

// infermedica

#hf-iframe {
  width: 100%;
  min-height: 810px;
  border: 0;
}

#safehaven .v-input--is-disabled {
  background-color: #f4f6f9 !important;
  border: #a5abb3 !important;
}

@media screen and (max-width: 960px) {
  #hf-iframe {
    width: 100%;
    height: 100%;
    border: 0;
  }
}

#cct-iframe {
  width: 100%;
  height: 900px;
  border: 0;
}

// body site picker
.body-area {
  cursor: pointer;
  opacity: 0;
}

.body-area-selected {
  cursor: pointer;
  opacity: 1;
}

.body-area.highlighted {
  opacity: 0.2;
}

.body-area:hover {
  opacity: 0.5;
}

// utility

.imagesClass {
  width: 25px;
  margin: 0 10px;
}
.v-application .my-auto-Saafe {
  margin-inline-start: auto;
}
.clickable {
  cursor: pointer;
}
.clickableSaafe {
  width: 100%;
  cursor: pointer;
}

.truncate {
  max-width: 1px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
// truncate after 2 lines
.truncate2 {
  display: -webkit-box;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}
// truncate after 3 lines
.truncate3 {
  display: -webkit-box;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}
// non-selectable text
.noselect {
  -webkit-touch-callout: none; /* iOS Safari */
  -webkit-user-select: none; /* Safari */
  -khtml-user-select: none; /* Konqueror HTML */
  -moz-user-select: none; /* Old versions of Firefox */
  -ms-user-select: none; /* Internet Explorer/Edge */
  user-select: none; /* Non-prefixed version, currently
                                  supported by Chrome, Edge, Opera and Firefox */
}

// Bootstrap like width/height helper classes
.w-100 {
  width: 100%;
}

.h-100 {
  height: 100%;
}

#safehaven .v-carousel {
  max-width: 460px !important;
  height: 97vh !important;
}
#safehaven .v-carousel .v-carousel__item {
  height: 97vh !important;
}

.v-btn--icon.v-size--small .v-icon {
  color: #c4c4c4 !important;
  opacity: 1 !important;
}
.v-item--active.v-btn--icon.v-size--small .v-icon {
  color: #0476d9 !important;
}
</style>
